import { Component } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';

import { AppSettings } from '../app.settings';
import { Donation, DonationType } from '../transaction-store/donation';
import { OptInActions, OptIns } from '../transaction-store/opt-ins';

@Component({
  selector: 'app-opt-ins',
  template: `
    <section class="container" *ngIf="show.anonymous || show.fee || show.email">
      <div
        *ngIf="show.anonymous && show.fromFundraiser"
        class="select-container"
      >
        <select-field
          class="col-12 col-sm"
          label="Anonymity"
          [items]="[
            {
              val: anonymityValues.SHOW_FOR_EVERYONE,
              text: 'Show my name publicly'
            },
            {
              val: anonymityValues.SHOW_FOR_FUNDRAISER,
              text: 'Show my name to this fundraiser only'
            },
            {
              val: anonymityValues.HIDE_FOR_EVERYONE,
              text: 'Hide my name from everyone'
            }
          ]"
          [ngModel]="donorAnonymity"
          (ngModelChange)="handleDonorAnonymityToggle($event)"
        >
        </select-field>
      </div>
      <div
        *ngIf="show.anonymous && !show.fromFundraiser"
        class="checkbox-container"
      >
        <div
          [attr.aria-label]="anonymous ? 'checked' : 'unchecked'"
          class="icon icon-inline"
          [ngClass]="anonymous ? 'icon-checked' : 'icon-uncheck'"
          (click)="onChange('anonymous')"
        ></div>
        <label for="donate-anonymous">{{
          anonymousMessage || 'I would like my donation to be anonymous'
        }}</label>
      </div>
      <div *ngIf="show.fee" class="checkbox-container">
        <div
          [attr.aria-label]="fees ? 'checked' : 'unchecked'"
          class="icon icon-inline"
          [ngClass]="fees ? 'icon-checked' : 'icon-uncheck'"
          (click)="onChange('fees')"
        ></div>
        <label for="pay-fees">{{ feeMessage }}</label>
      </div>
      <div *ngIf="show.email" class="checkbox-container">
        <div
          [attr.aria-label]="emails ? 'checked' : 'unchecked'"
          class="icon icon-inline"
          [ngClass]="emails ? 'icon-checked' : 'icon-uncheck'"
          type="checkbox"
          (click)="onChange('emails')"
        ></div>
        <label for="keep-emails">{{ emailMessage }}</label>
      </div>
    </section>
  `
})
export class OptInsComponent {
  @Select() donation$: Observable<Donation>;
  @Select() optIns$: Observable<OptIns>;
  feeAmount = 1;
  feeMessage = '';
  emailMessage = '';
  show: {
    fee: boolean;
    email: boolean;
    anonymous: boolean;
    fromFundraiser: boolean;
  };
  anonymous = false;
  anonymityValues = {
    SHOW_FOR_EVERYONE: 'showForEveryone',
    SHOW_FOR_FUNDRAISER: 'showForFundraiser',
    HIDE_FOR_EVERYONE: 'hideFromEveryone'
  };
  donorAnonymity = '';
  showForFundraiser = null;
  fees = false;
  emails = false;
  anonymousMessage = '';
  constructor(
    private settings: AppSettings,
    private currency: CurrencyPipe,
    private store: Store
  ) {
    const properties = this.settings.getValue('properties');
    const org = this.settings.getValue('organization');
    const rawFeeMessage =
      properties.donor_pays_fee_message ||
      'I would like to add {{amount}} to help cover the transaction cost';
    this.emailMessage =
      (properties.email_opt_in_message &&
        properties.email_opt_in_message.replace(
          '{{organization.name}}',
          org.name
        )) ||
      `I agree to receive future email communications from ${org.name}`;
    this.show = {
      fee: properties.donor_pays_fee,
      email: properties.show_email_opt,
      anonymous: properties.show_hide_name,
      fromFundraiser: properties.p2p_fundraiser_id
    };
    this.anonymousMessage = properties.anonymous_donor_message;
    this.fees = (this.show.fee && properties.donor_pays_fee_default) || false;
    // update defaulting of donor anonymity
    if (this.show.anonymous) {
      if (this.show.fromFundraiser) {
        this.donorAnonymity = this.anonymityValues.SHOW_FOR_EVERYONE;
        this.handleDonorAnonymityToggle(this.anonymityValues.SHOW_FOR_EVERYONE);
      } else {
        this.anonymous =
          (this.show.anonymous && properties.anonymous === 'true') || false;
        this.store.dispatch(new OptInActions.SetAnonymous(this.anonymous));
      }
    }

    const cashFixed = org.attributes['config.donor_fee.cash_fixed'];
    const cashRatio = org.attributes['config.donor_fee.cash_ratio'];
    const checkFixed = org.attributes['config.donor_fee.echeck_fixed'];
    const checkRatio = org.attributes['config.donor_fee.echeck_ratio'];
    const calculateFee = (amount, ratio, fixed) => {
      return this.roundHalfEven((amount * ratio + fixed) * 100.0) / 100.0;
    };
    this.donation$.subscribe(donation => {
      if (donation.type === DonationType.Check) {
        this.feeAmount = calculateFee(donation.amount, checkRatio, checkFixed);
      } else {
        this.feeAmount = calculateFee(donation.amount, cashRatio, cashFixed);
      }
      this.feeMessage = rawFeeMessage.replace(
        '{{amount}}',
        this.currency.transform(
          this.feeAmount,
          org.currency_code,
          'symbol-narrow'
        )
      );

      this.store.dispatch(
        new OptInActions.SetPayFee({
          enabled: this.fees,
          amount: this.feeAmount
        })
      );
    });
    this.optIns$.subscribe(optIns => {
      this.fees = optIns.payFee;
      this.emails = optIns.emails;
      this.anonymous = optIns.anonymous;
      this.feeAmount = optIns.feeAmount;
    });
  }
  roundHalfEven(value) {
    // "banker's rounding", 1.5 -> 2, 2.5 -> 2, 3.5 -> 4 etc
    if (value % 0.5 !== 0) {
      return Math.round(value);
    }

    return Math.floor(value) % 2 === 0 ? Math.floor(value) : Math.round(value);
  }
  handleDonorAnonymityToggle(donorAnonymityValue) {
    // event is the selected value, set it in component state
    switch (donorAnonymityValue) {
      case this.anonymityValues.SHOW_FOR_EVERYONE:
        this.anonymous = false;
        this.showForFundraiser = true;
        break;
      case this.anonymityValues.SHOW_FOR_FUNDRAISER:
        this.anonymous = true;
        this.showForFundraiser = true;
        break;
      case this.anonymityValues.HIDE_FOR_EVERYONE:
        this.anonymous = true;
        this.showForFundraiser = false;
        break;
      default:
        console.error('No valid donor anonymity value selected.');
    }

    this.onChange('donorAnonymity');
  }
  onChange(field) {
    switch (field) {
      case 'anonymous':
        this.anonymous = !this.anonymous;
        this.store.dispatch(new OptInActions.SetAnonymous(this.anonymous));
        break;
      case 'donorAnonymity':
        // boolean toggling is handled outside this call.
        this.store.dispatch(
          new OptInActions.SetAnonymousAndFundraiser({
            anonymous: this.anonymous,
            showForFundraiser: this.showForFundraiser
          })
        );
      case 'emails':
        this.emails = !this.emails;
        this.store.dispatch(new OptInActions.SetEmails(this.emails));
        break;
      case 'fees':
        this.fees = !this.fees;
        this.store.dispatch(
          new OptInActions.SetPayFee({
            enabled: this.fees,
            amount: this.feeAmount
          })
        );
        break;
    }
  }
}
